import { SurveyModel } from "survey-core";

const ITALIC_PATTERN = /\*([^*]+)\*/g;

const wrapBareSubstitutions = (text: string): string =>
  text.replace(/(?<!\*)(\{[^{}]+})(?!\*)/g, "*$1*");

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Enables *italic* markdown in survey titles/descriptions so dynamic
 * answer substitutions can be emphasized, e.g.
 * "When did you last get a *{screen-type}*?"
 *
 * Also wraps bare `{question-name}` placeholders so they italicize
 * even when the template omitted the asterisks.
 *
 * The text passed to onTextMarkdown already has `{question-name}`
 * substitutions applied, so it can contain member-entered answers. The whole
 * string is HTML-escaped before the `<em>` tags are added, so the only markup
 * ever emitted is the `<em>` produced here. `options.html` is only set when
 * italic markup is present; plain strings keep survey-core's default text
 * rendering.
 */
export const enableSurveyTitleMarkdown = (survey: SurveyModel): void => {
  survey.getAllQuestions().forEach((question) => {
    if (typeof question.title === "string") {
      question.title = wrapBareSubstitutions(question.title);
    }
    if (typeof question.description === "string") {
      question.description = wrapBareSubstitutions(question.description);
    }
  });

  survey.onTextMarkdown.add((_sender, options) => {
    const text = options.text ?? "";
    if (!new RegExp(ITALIC_PATTERN.source).test(text)) {
      return;
    }
    options.html = escapeHtml(text).replace(ITALIC_PATTERN, "<em>$1</em>");
  });
};
